import { PubSubService } from '@ohif/core';
import { authHttp, HttpException } from '../http';
import { getLoginUrl } from '../config/roots';

export enum AccountRoleEnum {
  USER = 'USER',
  ENVIRONMENT_ADMIN = 'ENVIRONMENT_ADMIN',
  REVIEWER = 'REVIEWER',
}

export enum EnvironmentSizeEnum {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export type Account = {
  id: string;
  email: string;
  username: string;
  role: AccountRoleEnum;
  size: EnvironmentSizeEnum;
};

const EVENTS = {
  SESSION_CHANGED: 'event::sycai:SessionService:sessionChanged',
};

const VERIFY_SESSION_TIMEOUT_MS = 8000;

function verifySessionTimeoutSignal(): AbortSignal | undefined {
  return typeof AbortSignal?.timeout === 'function'
    ? AbortSignal.timeout(VERIFY_SESSION_TIMEOUT_MS)
    : undefined;
}

function isAccount(data: unknown): data is Account {
  return (
    Boolean(data) &&
    typeof data === 'object' &&
    typeof (data as { role?: unknown }).role === 'string' &&
    (data as { role: string }).role.length > 0
  );
}

export class SessionService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'sessionService',
    create: () => new SessionService(),
  };

  private account: Account | null = null;

  constructor() {
    super(EVENTS);
  }

  public getAccount(): Account | null {
    return this.account;
  }

  public getRole(): AccountRoleEnum | null {
    return this.account?.role ?? null;
  }

  public isReviewer(): boolean {
    return this.getRole() === AccountRoleEnum.REVIEWER;
  }

  public async verifySession(): Promise<Account> {
    try {
      const { data, status } = await authHttp.get<Account>('accounts/me', {
        signal: verifySessionTimeoutSignal(),
      });

      if (!isAccount(data)) {
        throw new HttpException(
          status,
          `accounts/me answered ${status} with a body that is not an Account`
        );
      }

      this.setAccount(data);
      return data;
    } catch (error) {
      this.setAccount(null);
      throw error;
    }
  }

  public async logOut(): Promise<void> {
    try {
      await authHttp.post('auth/log-out', {});
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // A failed log-out must not prevent the local teardown or the redirect.
    } finally {
      this.redirectToLogin();
    }
  }

  /**
   * The no-session counterpart of logOut(): there is nothing for auth/log-out to
   * invalidate, so skipping it removes a network round-trip that the viewer would
   * otherwise spend rendering its shell before the redirect lands.
   */
  public redirectToLogin(): void {
    this.setAccount(null);
    localStorage.removeItem('isAuthenticated');
    window.location.replace(getLoginUrl());
  }

  private setAccount(account: Account | null): void {
    this.account = account;
    this._broadcastEvent(EVENTS.SESSION_CHANGED, { role: account?.role ?? null });
  }
}
